// src/features/admin/adminBidSlice.js
import axios from '../../utils/axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { lifecycleError } from '../../utils/bookingLifecycle';
import { dedupeEntities } from '../../utils/collectionUtils';

// ────────────────────────────────────────────────
// Thunks (Admin-focused)
// ────────────────────────────────────────────────

export const getAllBids = createAsyncThunk(
  'adminBid/getAllBids',
  async ({ page = 1, limit = 20, status, practitionerId, jobId, active, sortBy, sortOrder }, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (status)         params.status = status;
      if (practitionerId) params.practitionerId = practitionerId;
      if (jobId)          params.jobId = jobId;
      if (active !== undefined) params.active = active;
      if (sortBy)         params.sortBy = sortBy;
      if (sortOrder)      params.sortOrder = sortOrder;

      const res = await axios.get('/bids/admin', { params });
      // Backend returns: { bids, pagination: { total, page, limit, pages, hasNext, hasPrev } }
      return res.data.data || res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch bids');
    }
  }
);

export const getBidById = createAsyncThunk(
  'adminBid/getBidById',
  async (bidId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`/bids/admin/${bidId}`);
      return res.data.data?.bid || res.data.bid;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch bid details');
    }
  }
);

export const updateBidStatus = createAsyncThunk(
  'adminBid/updateBidStatus',
  async ({ bidId, status }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`/bids/admin/${bidId}`, { status });
      return res.data.data?.bid || res.data.bid;
    } catch (err) {
      return rejectWithValue(lifecycleError(err, 'Failed to update bid status'));
    }
  }
);

export const updateBid = createAsyncThunk(
  'adminBid/updateBid',
  async ({ bidId, updates }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`/bids/admin/${bidId}`, updates);
      return res.data.data?.bid || res.data.bid;
    } catch (err) {
      return rejectWithValue(lifecycleError(err, 'Failed to update bid'));
    }
  }
);

export const deleteBid = createAsyncThunk(
  'adminBid/deleteBid',
  async (bidId, { rejectWithValue }) => {
    try {
      const res = await axios.delete(`/bids/admin/${bidId}`);
      return { bidId, message: res.data.message };
    } catch (err) {
      return rejectWithValue(lifecycleError(err, 'Failed to delete bid'));
    }
  }
);

/**
 * Optional: Get bid statistics (counts by status)
 */
export const getBidStats = createAsyncThunk(
  'adminBid/getBidStats',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get('/bids/admin/stats');
      return res.data.data?.stats || res.data.stats;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch bid statistics');
    }
  }
);

// ────────────────────────────────────────────────
// Slice
// ────────────────────────────────────────────────

const initialState = {
  bids:               [],
  currentBid:         null,
  total:              0,
  currentPage:        1,
  pages:              0,
  limit:              20,
  stats:              null,           // { pending: X, accepted: Y, rejected: Z }
  loading:            false,
  actionLoading:      false,
  error:              null,
  lastActionBidId:    null,
};

const adminBidSlice = createSlice({
  name: 'adminBid',
  initialState,
  reducers: {
    clearAdminBidError: (state) => {
      state.error = null;
    },
    clearCurrentBid: (state) => {
      state.currentBid = null;
    },
  },
  extraReducers: (builder) => {
    // ── getAllBids ───────────────────────────────────────
    builder
      .addCase(getAllBids.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllBids.fulfilled, (state, action) => {
        state.loading = false;
        state.bids = dedupeEntities(action.payload.bids);
        state.total = action.payload.pagination?.total || 0;
        state.currentPage = action.payload.pagination?.page || 1;
        state.pages = action.payload.pagination?.pages || 1;
        state.limit = action.payload.pagination?.limit || 20;
      })
      .addCase(getAllBids.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── getBidById ───────────────────────────────────────
    builder
      .addCase(getBidById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBidById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBid = action.payload;
      })
      .addCase(getBidById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // ── updateBidStatus ──────────────────────────────────
    builder
      .addCase(updateBidStatus.pending, (state, action) => {
        state.actionLoading = true;
        state.lastActionBidId = action.meta.arg.bidId;
      })
      .addCase(updateBidStatus.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        const id = updated._id;

        const index = state.bids.findIndex(b => b._id === id);
        if (index !== -1) {
          state.bids[index] = { ...state.bids[index], ...updated };
        }

        if (state.currentBid?._id === id) {
          state.currentBid = { ...state.currentBid, ...updated };
        }

        state.lastActionBidId = null;
      })
      .addCase(updateBidStatus.rejected, (state) => {
        state.actionLoading = false;
        state.lastActionBidId = null;
      });

    // ── updateBid (general admin update) ─────────────────
    builder
      .addCase(updateBid.pending, (state, action) => {
        state.actionLoading = true;
        state.lastActionBidId = action.meta.arg.bidId;
      })
      .addCase(updateBid.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        const id = updated._id;

        const index = state.bids.findIndex(b => b._id === id);
        if (index !== -1) {
          state.bids[index] = { ...state.bids[index], ...updated };
        }

        if (state.currentBid?._id === id) {
          state.currentBid = { ...state.currentBid, ...updated };
        }

        state.lastActionBidId = null;
      })
      .addCase(updateBid.rejected, (state) => {
        state.actionLoading = false;
        state.lastActionBidId = null;
      });

    // ── deleteBid ────────────────────────────────────────
    builder
      .addCase(deleteBid.pending, (state, action) => {
        state.actionLoading = true;
        state.lastActionBidId = action.meta.arg;
      })
      .addCase(deleteBid.fulfilled, (state, action) => {
        state.actionLoading = false;
        const { bidId } = action.payload;
        state.bids = state.bids.filter(b => b._id !== bidId);
        if (state.total > 0) state.total -= 1;
        if (state.currentBid?._id === bidId) {
          state.currentBid = null;
        }
        state.lastActionBidId = null;
      })
      .addCase(deleteBid.rejected, (state) => {
        state.actionLoading = false;
        state.lastActionBidId = null;
      });

    // ── getBidStats ──────────────────────────────────────
    builder
      .addCase(getBidStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const { clearAdminBidError, clearCurrentBid } = adminBidSlice.actions;

// Selectors
export const selectAdminBids          = (state) => state.adminBid.bids;
export const selectAdminBidsTotal     = (state) => state.adminBid.total;
export const selectAdminBidLoading    = (state) => state.adminBid.loading;
export const selectAdminBidError      = (state) => state.adminBid.error;
export const selectCurrentAdminBid    = (state) => state.adminBid.currentBid;
export const selectActionLoadingBid   = (state) => state.adminBid.actionLoading;
export const selectLastActionBidId    = (state) => state.adminBid.lastActionBidId;
export const selectBidStats           = (state) => state.adminBid.stats;

export default adminBidSlice.reducer;
